/**
 * Bollinger Band Width (BBW)
 *
 * Measures the width of Bollinger Bands relative to the middle band.
 * BBW = (Upper − Lower) / Middle × 100
 * Low values indicate a "squeeze" (potential breakout).
 *
 * Category: Volatility | Pane: Separate
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { getSource, computeSMA } from "./sma";

export const bbwIndicator: IndicatorDefinition = {
  id: "bbw",
  name: "Bollinger Band Width",
  shortName: "BBW",
  category: "volatility",
  pane: "separate",
  params: [
    { key: "period", label: "Period", type: "number", default: 20, min: 2, max: 500 },
    { key: "stdDev", label: "Std Deviation", type: "number", default: 2, min: 0.1, max: 10, step: 0.1 },
    { key: "color", label: "Color", type: "color", default: "#FF9800" },
  ],
  plots: [
    { key: "bbw", label: "BBW", color: "#FF9800", style: "line", lineWidth: 2, priceScaleId: "bbw" },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { period, stdDev } = params;
    const src = getSource(candles, "close");
    const sma = computeSMA(src, period);
    const data: { time: number; value: number }[] = [];

    for (let i = period - 1; i < src.length; i++) {
      const mean = sma[i];
      if (isNaN(mean) || mean === 0) continue;
      let sqSum = 0;
      for (let j = i - period + 1; j <= i; j++) {
        sqSum += (src[j] - mean) ** 2;
      }
      const std = Math.sqrt(sqSum / period);
      const upper = mean + stdDev * std;
      const lower = mean - stdDev * std;
      data.push({ time: candles[i].time, value: ((upper - lower) / mean) * 100 });
    }

    return [{ key: "bbw", data }];
  },
};
